import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Upload, Sparkles, ArrowLeft, Image as ImageIcon } from "lucide-react";
import { analyzePrescriptionImage, rx } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { trackEvent } from "../lib/analytics";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = r.result;
      const base64 = String(dataUrl).split(",")[1];
      resolve({ base64, mime: file.type });
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const nowLocalInput = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function NewPrescriptionPage() {
  const nav = useNavigate();
  const [stage, setStage] = useState("capture"); // capture | analyzing | review
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    medicine_name: "",
    dosage: "",
    frequency_per_day: 3,
    duration_days: 7,
    start_datetime: nowLocalInput(),
    instructions: "",
    route: "",
  });
  const [useCustomTimes, setUseCustomTimes] = useState(false);
  const [customTimes, setCustomTimes] = useState(["08:00", "14:00", "22:00"]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const camRef = useRef();

  async function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(jpeg|jpg|png|webp)/i.test(f.type)) {
      toast.error("Please upload a JPEG, PNG or WEBP image");
      return;
    }
    const { base64, mime } = await fileToBase64(f);
    setPreview(URL.createObjectURL(f));
    setStage("analyzing");
    try {
      const data = await analyzePrescriptionImage(base64, mime);
      setForm((prev) => ({
        ...prev,
        medicine_name: data.medicine_name || prev.medicine_name,
        dosage: data.dosage || prev.dosage,
        frequency_per_day: data.frequency_per_day || prev.frequency_per_day,
        duration_days: data.duration_days || prev.duration_days,
        instructions: data.instructions || prev.instructions,
        route: data.route || prev.route,
        tapering_schedule: (Array.isArray(data.tapering_schedule) && data.tapering_schedule.length > 0)
          ? data.tapering_schedule
          : prev.tapering_schedule,
      }));
      if (Array.isArray(data.tapering_schedule) && data.tapering_schedule.length > 0) {
        toast.success(`Label read — ${data.tapering_schedule.length}-step tapering schedule detected.`);
      } else {
        toast.success("Label read. Please review and confirm.");
      }
      setStage("review");
    } catch (err) {
      toast.error("Couldn't read the label — fill the form manually.");
      setStage("review");
    }
  }

  function skipToManual() {
    setStage("review");
  }

  async function save() {
    if (!form.medicine_name || !form.dosage || !form.frequency_per_day || !form.duration_days || !form.start_datetime) {
      toast.error("Please fill name, dosage, frequency, duration and start date");
      return;
    }
    setSaving(true);
    try {
      // convert local datetime input to ISO
      const startIso = new Date(form.start_datetime).toISOString();
      const ct = useCustomTimes ? customTimes.filter(t => /^\d{2}:\d{2}$/.test(t)) : null;
      const freq = ct && ct.length > 0 ? ct.length : Number(form.frequency_per_day);
      const payload = {
        ...form,
        start_datetime: startIso,
        frequency_per_day: freq,
        duration_days: Number(form.duration_days),
        custom_times: ct && ct.length > 0 ? ct : null,
        tapering_schedule: (Array.isArray(form.tapering_schedule) && form.tapering_schedule.length > 0)
          ? form.tapering_schedule
          : null,
      };
      const created = await rx.create(payload);
      trackEvent("prescription_created", {
        ai_parsed: !!form._ai_parsed,
        frequency_per_day: freq,
        duration_days: Number(form.duration_days),
      });
      toast.success("Prescription added");
      nav(`/prescriptions/${created.id}`);
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="animate-fade-up max-w-3xl mx-auto">
      <Link to="/prescriptions" className="inline-flex items-center gap-2 text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] mb-6" data-testid="back-to-rx-list">
        <ArrowLeft className="h-4 w-4" /> Back to prescriptions
      </Link>

      <span className="label-caps">New Prescription · Step {stage === "capture" ? "1 of 2" : "2 of 2"}</span>
      <h1 className="mt-2 text-3xl sm:text-4xl font-display font-black tracking-tighter">
        {stage === "capture" && "Snap or upload the label"}
        {stage === "analyzing" && "Reading the label…"}
        {stage === "review" && "Review the details"}
      </h1>

      {stage === "capture" && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <button
            type="button"
            onClick={() => camRef.current?.click()}
            data-testid="capture-camera-button"
            className="card-tactile rounded-2xl p-8 flex flex-col items-start text-left group hover:border-[color:var(--primary)]"
          >
            <div className="h-11 w-11 rounded-xl bg-[color:var(--primary-soft)] flex items-center justify-center mb-4">
              <Camera className="h-6 w-6 text-[color:var(--primary-ink)]" />
            </div>
            <div className="font-display font-bold text-lg">Take a photo</div>
            <div className="text-sm text-[color:var(--text-muted)] mt-1">Use your camera to snap the medication label.</div>
            <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
          </button>

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="capture-upload-button"
            className="card-tactile rounded-2xl p-8 flex flex-col items-start text-left group hover:border-[color:var(--primary)]"
          >
            <div className="h-11 w-11 rounded-xl bg-[color:var(--warning-bg)] flex items-center justify-center mb-4">
              <Upload className="h-6 w-6 text-[color:var(--warning)]" />
            </div>
            <div className="font-display font-bold text-lg">Upload an image</div>
            <div className="text-sm text-[color:var(--text-muted)] mt-1">JPEG, PNG or WEBP. We read it with AI.</div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" />
          </button>

          <button
            type="button"
            onClick={skipToManual}
            data-testid="capture-manual-button"
            className="md:col-span-2 text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] underline underline-offset-4 py-4 text-center"
          >
            Skip — enter details manually
          </button>
        </div>
      )}

      {stage === "analyzing" && (
        <div className="mt-8 card-tactile rounded-2xl p-8 flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-[color:var(--primary-soft)] flex items-center justify-center pulse-accent">
            <Sparkles className="h-6 w-6 text-[color:var(--primary-ink)]" />
          </div>
          <div>
            <div className="font-display font-bold">Reading your label with Gemini 3 Flash</div>
            <div className="text-sm text-[color:var(--text-muted)]">Extracting medicine, dosage, and schedule…</div>
          </div>
        </div>
      )}

      {stage === "review" && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            {preview ? (
              <div className="card-tactile rounded-2xl overflow-hidden">
                <img src={preview} alt="Prescription" className="w-full h-56 object-cover" />
                <div className="p-4 flex items-center gap-2 text-xs text-[color:var(--text-muted)]">
                  <ImageIcon className="h-4 w-4" /> AI-analyzed
                </div>
              </div>
            ) : (
              <div className="card-tactile rounded-2xl p-6 text-sm text-[color:var(--text-muted)]">Manual entry — no image attached.</div>
            )}
          </div>

          <div className="md:col-span-2 card-tactile rounded-2xl p-6 space-y-4">
            {Array.isArray(form.tapering_schedule) && form.tapering_schedule.length > 0 && (
              <div
                data-testid="form-tapering-preview"
                className="rounded-lg p-4 border-l-4"
                style={{
                  borderLeftColor: "var(--primary)",
                  background: "color-mix(in srgb, var(--primary) 6%, transparent)",
                }}
              >
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div>
                    <div className="label-caps" style={{ color: "var(--primary-ink)" }}>Tapering schedule detected</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                      {form.tapering_schedule.length} bands — saved with the prescription.
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="form-tapering-clear"
                    onClick={() => setForm({ ...form, tapering_schedule: null })}
                    className="text-xs font-semibold hover:underline shrink-0"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {form.tapering_schedule.map((b, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="font-mono" style={{ color: "var(--text-muted)" }}>
                        Days {b.day_from}–{b.day_to}
                      </span>
                      <span className="font-semibold text-[color:var(--text-main)]">{b.dose_amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="med-name">Medicine name</Label>
              <Input id="med-name" data-testid="form-med-name" value={form.medicine_name} onChange={(e) => setForm({ ...form, medicine_name: e.target.value })} placeholder="Amoxicillin" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dosage">Dosage</Label>
                <Input id="dosage" data-testid="form-dosage" value={form.dosage} onChange={(e) => setForm({ ...form, dosage: e.target.value })} placeholder="1 tablet" />
              </div>
              <div>
                <Label htmlFor="route">Route</Label>
                <Input id="route" data-testid="form-route" value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })} placeholder="oral" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="freq">Doses per day</Label>
                <Input id="freq" type="number" min="1" max="12" data-testid="form-frequency" value={useCustomTimes ? customTimes.length : form.frequency_per_day} disabled={useCustomTimes} onChange={(e) => setForm({ ...form, frequency_per_day: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="dur">Duration (days)</Label>
                <Input id="dur" type="number" min="1" max="365" data-testid="form-duration" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
              </div>
            </div>
            <div className="rounded-lg border border-[color:var(--border-subtle)] p-4 bg-[color:var(--surface-2)]">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={useCustomTimes} onChange={(e) => setUseCustomTimes(e.target.checked)} data-testid="form-use-custom" className="accent-[color:var(--primary)]" />
                <span className="text-sm font-semibold">Use custom times instead of even split</span>
              </label>
              {useCustomTimes && (
                <div className="mt-3 space-y-2">
                  {customTimes.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input type="time" value={t} onChange={(e) => {
                        const c = [...customTimes]; c[i] = e.target.value; setCustomTimes(c);
                      }} data-testid={`form-custom-time-${i}`} className="max-w-[140px]" />
                      {customTimes.length > 1 && (
                        <button type="button" onClick={() => setCustomTimes(customTimes.filter((_, idx) => idx !== i))} data-testid={`form-remove-time-${i}`} className="text-xs text-[color:var(--accent-ink)] hover:underline">Remove</button>
                      )}
                    </div>
                  ))}
                  {customTimes.length < 12 && (
                    <button type="button" onClick={() => setCustomTimes([...customTimes, "20:00"])} data-testid="form-add-time" className="text-sm text-[color:var(--primary-ink)] font-semibold hover:underline">+ Add time</button>
                  )}
                  <p className="text-xs text-[color:var(--text-muted)]">Each time of day will be used every day of the course.</p>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="start">Start date & time</Label>
              <Input id="start" type="datetime-local" data-testid="form-start" value={form.start_datetime} onChange={(e) => setForm({ ...form, start_datetime: e.target.value })} />
              <p className="text-xs text-[color:var(--text-muted)] mt-1">Doses in the past will be auto-marked as taken (asterisk). Doses before now but after creation are also taken automatically.</p>
            </div>
            <div>
              <Label htmlFor="inst">Special instructions</Label>
              <Textarea id="inst" data-testid="form-instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="Take with food" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => nav("/prescriptions")} data-testid="cancel-rx">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="save-rx" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white">
                {saving ? "Saving…" : "Create schedule"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
